import { Injectable } from '@angular/core';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import {
  addEntities,
  deleteEntities,
  selectAllEntities,
  selectAllEntitiesApply,
  setEntities,
  updateEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { persistState } from '@ngneat/elf-persist-state';
import * as localforage from 'localforage';
import * as moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, tap, map, switchMap } from 'rxjs/operators';
import { REFRESH_TIME_IN_MINUTES } from 'src/app/shared/configs/global-values.constants';
import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';
import {
  IDeleteParams,
  IStoreProps,
  IListParams,
  IPaginationState,
  IScrollPosition,
} from 'src/app/talent-litmus-core/common/common.interface';
import { ISliderGame } from './slider-game.interface';
import { PageEvent } from '@angular/material/paginator';

// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'slider-games';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IStoreProps>({ lastRefreshed: undefined, search: '' }),
  withProps<IPaginationState>({
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  }),
  withEntities<ISliderGame, '_id'>({ idKey: '_id' }),
  withProps<IScrollPosition>({ x: 0, y: 0 }),
  withProps<{ selectedId: string }>({ selectedId: null }),
  withProps<{ searchParam: string }>({ searchParam: '' }),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class SliderGameCoreService {
  private _loading$ = new BehaviorSubject<boolean>(false);
  private _filter$ = store.pipe(select(({ search }) => search));

  /**
   * @name sliderGameList$
   * @description emits stored list of the companies
   */
  sliderGameList$ = store.pipe(selectAllEntities());
  paginationState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );
  totalCount: number;

  visibleSliderGameList$ = this._filter$.pipe(
    switchMap((filter) => {
      return store.pipe(
        selectAllEntitiesApply({
          filterEntity(sliderGame: ISliderGame) {
            return (
              sliderGame.name
                .toLowerCase()
                .includes(filter.trim().toLowerCase()) ||
              sliderGame._id.toLowerCase().includes(filter.trim().toLowerCase())
            );
          },
        }),
      );
    }),
  );

  public get loading$(): Observable<boolean> {
    return this._loading$.asObservable();
  }

  constructor(private _api: ApiBaseService) {
    this.refresh();
  }

  public updateFilter(search: IStoreProps['search']): void {
    store.update(setProp('search', search));
  }

  public getSliderGameList(params?: IListParams): void {
    this._loading$.next(true);
    this._api
      .get(`slider-game/list?limit=1000&skip=0`, params || {})
      .pipe(debounceTime(10000))
      .subscribe((data: ISliderGameListResponse) => {
        this.totalCount = data.totalCount;
        store.update(
          setEntities(data.list),
          setProp('lastRefreshed', new Date()),
        );
        this._loading$.next(false);
      });
  }
  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getSliderGameList();
    }
  }

  public getSliderGame(sliderGameId: string): Observable<ISliderGame> {
    return this._api.get(`slider-game/${sliderGameId}`);
  }
  public addSliderGame(sliderGame: ISliderGame): Observable<ISliderGame> {
    console.log('sliderGame: ', sliderGame);
    return this._api
      .post(`slider-game`, sliderGame)
      .pipe(tap((data: ISliderGame) => store.update(addEntities([data]))));
  }

  public updateSliderGame(sliderGame: ISliderGame): Observable<ISliderGame> {
    return this._api.put(`slider-game`, sliderGame).pipe(
      map((data: ArrayBuffer) => data as any),
      tap((data: ISliderGame) =>
        store.update(
          updateEntities(data._id, (entity) => ({
            ...entity,
            name: data.name,
          })),
        ),
      ),
    );
  }
  public deleteSliderGame(data: IDeleteParams): Observable<any> {
    return this._api
      .delete('slider-game', { body: data })
      .pipe(tap(() => store.update(deleteEntities(data['_id']))));
  }

  moveToLastPage(): void {
    let length: number;
    let pageSize: number;
    let previousPageIndex: number;
    this.paginationState$.subscribe((state) => {
      length = state.length;
      pageSize = state.pageSize;
      previousPageIndex = state.previousPageIndex;
    });
    this.pageChangeEvent({
      length,
      pageIndex: Math.ceil(length / pageSize),
      pageSize,
      previousPageIndex,
    });
  }

  pageChangeEvent(event: PageEvent): void {
    store.update((state) => ({
      ...state,
      length: event.length,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
    }));
  }

  setScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      x: scrollPostion[0],
      y: scrollPostion[1],
    }));
  }

  setSelectedId(id: string): void {
    store.update((state) => ({
      ...state,
      selectedId: id,
    }));
  }

  setSearchParam(searchParam: string): void {
    store.update((state) => ({
      ...state,
      searchParam: searchParam,
    }));
  }
}

interface ISliderGameListResponse {
  list: ISliderGame[];
  totalCount: number;
}
